import type { AuditStatus, UIAudit } from "@/packages/types";
import type { AuditAction } from "./use-audit-status-change";

export interface AuditStatusConfig {
  label: string;
  dotColor: string;
  badgeBg: string;
  badgeText: string;
  sortOrder: number;
}

export const AUDIT_STATUS_CONFIG: Record<AuditStatus, AuditStatusConfig> = {
  pending: {
    label: "Pending",
    dotColor: "#6C6C70",
    badgeBg: "rgba(108,108,112,0.15)",
    badgeText: "#A1A1A6",
    sortOrder: 1,
  },
  in_progress: {
    label: "In Progress",
    dotColor: "#E8734A",
    badgeBg: "rgba(232,115,74,0.15)",
    badgeText: "#E8734A",
    sortOrder: 2,
  },
  submitted: {
    label: "Submitted",
    dotColor: "#3b82f6",
    badgeBg: "rgba(59,130,246,0.15)",
    badgeText: "#3b82f6",
    sortOrder: 3,
  },
  approved: {
    label: "Approved",
    dotColor: "#22c55e",
    badgeBg: "rgba(34,197,94,0.15)",
    badgeText: "#22c55e",
    sortOrder: 4,
  },
  rejected: {
    label: "Rejected",
    dotColor: "#ef4444",
    badgeBg: "rgba(239,68,68,0.15)",
    badgeText: "#ef4444",
    sortOrder: 5,
  },
  archived: {
    label: "Archived",
    dotColor: "#6C6C70",
    badgeBg: "rgba(108,108,112,0.15)",
    badgeText: "#6C6C70",
    sortOrder: 6,
  },
};

export const SORTED_STATUSES: AuditStatus[] = (
  Object.entries(AUDIT_STATUS_CONFIG) as [AuditStatus, AuditStatusConfig][]
)
  .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
  .map(([status]) => status);

export function groupAuditsByStatus(
  audits: UIAudit[],
): Map<AuditStatus, UIAudit[]> {
  const map = new Map<AuditStatus, UIAudit[]>();

  for (const status of SORTED_STATUSES) {
    map.set(status, []);
  }

  for (const audit of audits) {
    const group = map.get(audit.status);
    if (group) {
      group.push(audit);
    }
  }

  return map;
}

export function getAuditColumns(
  audits: UIAudit[],
): { id: AuditStatus; title: string; audits: UIAudit[] }[] {
  const grouped = groupAuditsByStatus(audits);

  return SORTED_STATUSES.map((status) => ({
    id: status,
    title: AUDIT_STATUS_CONFIG[status].label,
    audits: grouped.get(status) ?? [],
  }));
}

export interface AuditTransitionConfig {
  /** Label shown on the primary split button */
  primaryLabel: string;
  /** Status this action moves the audit to */
  targetStatus: AuditStatus;
  /** Whether this primary action is fully implemented in MVP */
  mvp: boolean;
  /** Whether rejection reason is required (only for reject) */
  requiresReason?: boolean;
}

export const AUDIT_TRANSITION_CONFIG: Partial<
  Record<AuditStatus, AuditTransitionConfig>
> = {
  pending: {
    primaryLabel: "Start Progress",
    targetStatus: "in_progress",
    mvp: true,
  },
  in_progress: {
    primaryLabel: "Submit for Review",
    targetStatus: "submitted",
    mvp: true,
  },
  submitted: {
    primaryLabel: "Approve",
    targetStatus: "approved",
    mvp: true,
  },
  approved: {
    primaryLabel: "Archive",
    targetStatus: "archived",
    mvp: true,
  },
  rejected: {
    primaryLabel: "Resubmit",
    targetStatus: "submitted",
    mvp: true,
  },
  archived: {
    primaryLabel: "Restore",
    targetStatus: "in_progress",
    mvp: true,
  },
};

const VALID_TRANSITIONS: Record<
  string,
  { action: AuditAction; label: string }
> = {
  // Forward transitions
  "pending->in_progress": { action: "move_back", label: "Start Progress" },
  "in_progress->submitted": { action: "submit", label: "Submit for Review" },
  "submitted->approved": { action: "approve", label: "Approve" },
  // Backward transitions
  "in_progress->pending": {
    action: "move_back",
    label: "Move back to Pending",
  },
  "submitted->in_progress": {
    action: "move_back",
    label: "Move back to In Progress",
  },
  // Non-adjacent business-critical transitions
  "submitted->rejected": { action: "reject", label: "Reject" },
  "rejected->submitted": { action: "resubmit", label: "Resubmit" },
  "approved->submitted": {
    action: "move_back",
    label: "Move back to Submitted",
  },
  "approved->archived": { action: "archive", label: "Archive" },
  "in_progress->archived": { action: "archive", label: "Archive" },
  "archived->in_progress": { action: "unarchive", label: "Restore" },
};

export function isValidStatusTransition(
  source: AuditStatus,
  target: AuditStatus,
): boolean {
  return `${source}->${target}` in VALID_TRANSITIONS;
}

export function getActionForTransition(
  source: AuditStatus,
  target: AuditStatus,
): AuditAction | undefined {
  return VALID_TRANSITIONS[`${source}->${target}`]?.action;
}

export function getTransitionLabel(
  source: AuditStatus,
  target: AuditStatus,
): string {
  return VALID_TRANSITIONS[`${source}->${target}`]?.label ?? "";
}

export function getRollbackTransitions(
  status: AuditStatus,
): { targetStatus: AuditStatus; action: AuditAction; label: string }[] {
  const rollbacks: {
    targetStatus: AuditStatus;
    action: AuditAction;
    label: string;
  }[] = [];
  for (const [key, value] of Object.entries(VALID_TRANSITIONS)) {
    const [source, target] = key.split("->") as [AuditStatus, AuditStatus];
    if (source !== status) continue;
    // Skip the primary forward transition (already shown as main button)
    const primaryTarget = AUDIT_TRANSITION_CONFIG[status]?.targetStatus;
    if (target === primaryTarget) continue;
    rollbacks.push({
      targetStatus: target,
      action: value.action,
      label: value.label,
    });
  }
  return rollbacks;
}

export function formatDeadline(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
